import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  input,
  OnDestroy,
  OnInit,
  output,
  inject,
} from '@angular/core';
import { InPostPointSelect } from '../../types/in-post-point-select';
import { InPostPointLocation } from '../../types/in-post-point-location';
import { isEmptyString, isNil, Nillable } from '@web/utils';

/**
 * Komponent zaimplementowany na podstawie biblioteki do obsługi wyboru paczkomatu napisanej w Angular.
 * Link do repozytorium: https://github.com/rafalik2pl/inpost-geowidget-angular/blob/main/projects/inpost-geowidget-angular/src/lib/inpost-geowidget-angular.component.ts
 */
@Component({
  selector: 'app-in-post-point-select-widget',
  templateUrl: './in-post-point-select-widget.component.html',
  styleUrl: './in-post-point-select-widget.component.scss',
})
export class InPostPointSelectWidgetComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private el = inject(ElementRef);

  public static index = 0;
  public uid = '';

  public readonly location = input<Nillable<InPostPointLocation>>(null);
  public readonly token = input.required<string>();
  public readonly identifier = input<string>('Geo1');
  public readonly international = input<boolean>(false);
  public readonly country = input.required<string>();
  public readonly language = input<string>('pl');
  public readonly config = input<string>('parcelcollect');
  public readonly sandbox = input<boolean>(false);
  /* prettier-ignore */
  public readonly pointSelectEvent = output<InPostPointSelect>();
  public readonly apiReady = output();
  /* prettier-ignore */
  protected readonly uniqueIdentifier = computed(() => `${this.identifier()}_${InPostPointSelectWidgetComponent.index}`);
  private readonly sdkScriptId = '767d2954-9470-4aaa-9b7d-5b7aae5df938';
  private readonly sdkStylesheetId = '91e1a047-c187-46d3-9e37-5af543f07f80';

  public constructor() {
    ++InPostPointSelectWidgetComponent.index;
  }

  public ngOnInit(): void {
    this.uid = `${this.uniqueIdentifier()}_Wrapper`;

    if (!this.token()) {
      console.warn(
        'Brak tokenu dla widgetu. Proszę zweryfikować poprawność konfiguracji: https://dokumentacja-inpost.atlassian.net/wiki/spaces/PL/pages/50069505/Geowidget+v5.',
      );
    }
  }

  public ngAfterViewInit(): void {
    this.loadInPostSdk().then(() => {
      this.loadInPostStylesheet().then(() => {
        this.initializeInPostWidget();
      });
    });
  }

  public ngOnDestroy() {
    const geowidget = document.querySelector(this.uid);
    const script = document.getElementById(this.sdkScriptId);
    const stylesheet = document.getElementById(this.sdkStylesheetId);

    if (geowidget) {
      geowidget.remove();
    }

    if (script) {
      script.remove();
    }

    if (stylesheet) {
      stylesheet.remove();
    }
  }

  private initializeInPostWidget(): void {
    setTimeout(() => {
      this.render();

      const geowidget = this.el.nativeElement.querySelector(
        `#${this.uniqueIdentifier()}`,
      );

      geowidget.addEventListener(
        'inpost.geowidget.init',
        (event: CustomEvent) => {
          const api = event.detail.api;

          this.apiReady.emit(api);

          const coords = this.location();

          if (!isNil(coords)) {
            const { longitude, latitude } = coords;

            api.changePosition({ longitude, latitude }, 18);
          }

          api.addPointSelectedCallback(
            (point: {
              name: string;
              address: {
                line1: string;
                line2: string;
              };
              country: string;
              location: {
                latitude: number;
                longitude: number;
              };
              address_details: {
                city: string;
                province: string;
                post_code: string;
                street: string;
                building_number: string;
                flat_number: string;
              };
            }) => {
              const { name, address_details, country } = point;
              const {
                building_number,
                flat_number,
                street,
                post_code,
                city,
                province,
              } = address_details;

              const domesticName = (): string => {
                const prefix = 'PL_';

                if (name.toUpperCase().startsWith(prefix)) {
                  return name.slice(prefix.length);
                }

                return name;
              };

              const internationalDescription = (): string => {
                return `${name}, ${street}, ${post_code} ${city}, ${country}`;
              };

              const domesticDescription = (): string => {
                const numbers = [building_number, flat_number]
                  .filter(Boolean)
                  .join('/');

                return `${domesticName()}, ${street} ${numbers}, ${post_code} ${city}, ${province}`;
              };

              this.pointSelectEvent.emit(
                new InPostPointSelect({
                  description: this.international()
                    ? internationalDescription()
                    : domesticDescription(),
                  name: point.name,
                  location: new InPostPointLocation({
                    longitude: point.location.longitude,
                    latitude: point.location.latitude,
                  }),
                  address: {
                    city: point.address_details.city,
                    street: point.address_details.street,
                    postCode: point.address_details.post_code,
                    province: point.address_details.province,
                    buildingNumber: point.address_details.building_number,
                    flatNumber: point.address_details.flat_number,
                  },
                }),
              );
            },
          );
        },
      );
    });
  }

  private render(): void {
    const wrapperDiv = document.getElementById(this.uid);

    if (wrapperDiv) {
      const geoCustomElement = document.createElement('inpost-geowidget');

      geoCustomElement.setAttribute('token', this.token());
      geoCustomElement.setAttribute('language', this.language());
      geoCustomElement.setAttribute('config', this.config());
      geoCustomElement.setAttribute('id', `${this.uniqueIdentifier()}`);

      const country = this.country();

      if (!isEmptyString(country)) {
        geoCustomElement.setAttribute('country', this.country());
      }

      wrapperDiv.appendChild(geoCustomElement);
    } else {
      console.warn('Geowidget wrapper object not found.');
    }
  }

  private loadInPostSdk(): Promise<void> {
    return new Promise((resolve, reject) => {
      const existingScript = document.getElementById(this.sdkScriptId);

      if (existingScript) {
        return resolve();
      }

      const script = document.createElement('script');

      script.id = this.sdkScriptId;
      script.src = this.sdkScriptUrl();
      script.onload = () => {
        resolve();
      };
      script.onerror = () => {
        reject(new Error('Nie udało się załadować SDK InPost.'));
      };
      document.body.appendChild(script);
    });
  }

  private loadInPostStylesheet(): Promise<void> {
    return new Promise((resolve, reject) => {
      const existingStylesheet = document.getElementById(this.sdkStylesheetId);

      if (existingStylesheet) {
        resolve();
      }

      const link = document.createElement('link');

      link.id = this.sdkStylesheetId;
      link.rel = 'stylesheet';
      link.type = 'text/css';
      link.href = this.stylesheetUrl();
      link.onload = () => {
        resolve();
      };
      link.onerror = () => {
        reject(new Error('Nie udało się załadować arukuszy styli InPost.'));
      };
      document.head.appendChild(link);
    });
  }

  private sdkScriptUrl(): string {
    return this.sandbox()
      ? 'https://sandbox-global-geowidget-sdk.easypack24.net/inpost-geowidget.js'
      : 'https://geowidget.inpost-group.com/inpost-geowidget.js';
  }

  private stylesheetUrl(): string {
    return this.sandbox()
      ? 'https://sandbox-global-geowidget-sdk.easypack24.net/inpost-geowidget.css'
      : 'https://geowidget.inpost-group.com/inpost-geowidget.css';
  }
}
